import { useWindowSize } from '@uidotdev/usehooks'
import type { ChartData, ChartOptions, Point } from 'chart.js'
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js'
import { Line } from 'react-chartjs-2'

import { useIsDarkTheme } from '@/utils/useTheme'

import { EmptyChartData } from './empty-chart-data.tsx'
import type { ToggleChartOptions } from './types'
import {
  ChartFonts,
  customToolTip,
  dateLabels,
  getAxisCurrency,
  getTooltipCurrency,
  gridLineColor,
  multipleLines,
} from './utils'

type LineChartOptions = ChartOptions<'line'>
interface LineChartProps {
  data: ChartData<'line', (number | Point | null)[], unknown>
  options?: LineChartOptions
  toggleOptions?: ToggleChartOptions
  showCents?: boolean
  hideZeros?: boolean
}

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineController,
  LineElement,
  Title,
  Tooltip,
  Legend,
)

/**
 * LineChart Component
 *
 * @example
 *
 * ```jsx
 * const data: ChartData<"line"> = {
 *   labels: ["Jan", "Feb", "Mar"],
 *   datasets: [
 *     {
 *       label: "Dataset 1",
 *       data: [12, 19, 3],
 *       borderColor: "blue",
 *     },
 *   ],
 * };
 *
 * <LineChart data={data} />
 * ```
 *
 * @param optons Additional options for the chart.
 * @param data The chart data.
 */
export const LineChart = ({
  options,
  data,
  toggleOptions,
  showCents,
  hideZeros,
}: LineChartProps) => {
  const isDark = useIsDarkTheme()
  const { width } = useWindowSize()

  const isMobile = (width || 0) < 640
  const hasMultipleLines = data.datasets.length > 1

  const chartOptions = {
    ...options,
    responsive: true,

    elements: {
      // Data points
      point: {
        radius: 0,
        hoverRadius: 6,
        hitRadius: 10,
      },
      line: {
        tension: 0.25,
        cubicInterpolationMode: 'monotone',
      },
    },

    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: !toggleOptions?.hideTooltip,
        ...customToolTip,
        ...(toggleOptions?.isTooltipCurrency && getTooltipCurrency(showCents)),
        ...(hasMultipleLines &&
          multipleLines(toggleOptions?.isTooltipCurrency, showCents)),
        ...(hideZeros && {
          filter: ({ dataIndex, datasetIndex }) =>
            data.datasets[datasetIndex].data[dataIndex] !== 0,
        }),
        ...options?.plugins?.tooltip,
      },
    },

    scales: {
      ...options?.scales,
      x: {
        ...(toggleOptions?.dateLabels &&
          data.labels &&
          dateLabels(data.labels)),
        display: !toggleOptions?.hideLegend,
        beginAtZero: true,
        // Hide bottom border
        border: {
          display: false,
        },
        grid: {
          display: false,
        },
        ticks: {
          font: ChartFonts(isDark, isMobile).bodyFont,
          color: ChartFonts(isDark).color,
          autoSkip: true,
          maxTicksLimit: 6,
          maxRotation: 0,
          minRotation: 0,
        },
        offset: options?.scales?.x?.offset ?? true,
      },

      y: {
        ...options?.scales?.y,
        display: !toggleOptions?.hideLegend,
        beginAtZero: true,
        // Hide left border
        border: {
          display: false,
          dash: [8, 5],
        },
        grid: {
          // Background Grid
          color: gridLineColor(isDark).color,
          display: true,
          drawOnChartArea: true,
          lineWidth: 1.5,
        },
        ticks: {
          // Y Labels
          display: !toggleOptions?.hideLabels,
          font: ChartFonts(isDark, isMobile).bodyFont,
          color: ChartFonts(isDark).color,
          precision: 0,
          maxTicksLimit: 7,
          align: 'end',
          crossAlign: 'far',
          padding: 5,
          ...(toggleOptions?.isTooltipCurrency && getAxisCurrency(showCents)),
          ...options?.scales?.y?.ticks,
        },
        offset: true,
      },
    },
  } as LineChartOptions

  return !data || (data && data.labels?.length === 0) ? (
    <EmptyChartData isFetching={false} />
  ) : (
    <Line
      options={chartOptions}
      data={data}
    />
  )
}
